import type { PortalLookDto } from '@bulk/validation';
import { readableColor } from '@/lib/utils';
import { fontStack, memberDesignCss, siteControlsCss, uiBoldCss, uiScaleCss } from '@/surfaces/public/design';
import { contrast, mixHex, schemeColors } from '@/surfaces/public/schemes';

const BODY_TEXT_CONTRAST = 4.5;
const UI_CONTRAST = 3;
const BLACK = '#111111';
const WHITE = '#ffffff';
const PURE_BLACK = '#000000';

/** Where each of Bulk's slate steps sits between the card (0) and the text colour (1); the steps are what hovers, tracks, placeholders and dividers are drawn with. */
const SLATE_STEPS: Record<string, number> = { 50: 0.04, 100: 0.08, 200: 0.14, 300: 0.22, 400: 0.38, 500: 0.5, 600: 0.62, 700: 0.74, 800: 0.86, 900: 0.94, 950: 1 };

export interface PortalLookTheme {
  tone: 'light' | 'dark';
  background: string;
  card: string;
  text: string;
  muted: string;
  accent: string;
  onAccent: string;
  focus: string;
  vars: Record<string, string>;
}

/** Bulk's readable colour on `background`, or whichever of black and white reaches 4.5:1; pure black is the last resort, for the mid-tones where #111111 falls just short. */
function onColor(background: string): string {
  const candidates = [readableColor(background), BLACK, WHITE, PURE_BLACK];
  return candidates.find((candidate) => contrast(candidate, background) >= BODY_TEXT_CONTRAST) ?? PURE_BLACK;
}

function firstThatShows(candidates: string[], against: string[], minimum: number): string | null {
  return candidates.find((candidate) => against.every((colour) => contrast(candidate, colour) >= minimum)) ?? null;
}

/** The softest step toward the text colour that still reads on both the page and the card. */
function mutedText(text: string, background: string, card: string): string {
  for (let weight = 0.7; weight < 1; weight += 0.05) {
    const candidate = mixHex(text, card, weight);
    if (contrast(candidate, card) >= BODY_TEXT_CONTRAST && contrast(candidate, background) >= BODY_TEXT_CONTRAST) return candidate;
  }
  return text;
}

/**
 * The Bulk tokens a gym's design redefines for the sign-in pages and member area: the page is the default
 * scheme's background, cards sit a step toward its text colour, and every slate step, divider and muted
 * text is mixed between the two, so Bulk's own components keep their contrast on any brand. The scheme's
 * text must reach 4.5:1 on the page and on the card, else black or white takes its place; the brand
 * colour fills the primary button with the label that reads on it, and rings focus only where it reaches 3:1.
 */
export function portalLookTheme(look: PortalLookDto): PortalLookTheme {
  const { design, branding } = look;
  const scheme = schemeColors('default', branding, design);
  const background = scheme.background;
  const dark = readableColor(background) === WHITE;
  const cardWeight = dark ? 0.09 : 0.05;
  const fallbackText = onColor(background);
  const tinted = (colour: string) => mixHex(colour, background, cardWeight);
  const reads = (colour: string) => contrast(colour, background) >= BODY_TEXT_CONTRAST && contrast(colour, tinted(colour)) >= BODY_TEXT_CONTRAST;
  const text = reads(scheme.text) ? scheme.text : fallbackText;
  const card = contrast(text, tinted(text)) >= BODY_TEXT_CONTRAST ? tinted(text) : background;
  const muted = mutedText(text, background, card);
  const line = mixHex(text, background, dark ? 0.2 : 0.16);
  const accent = scheme.accent;
  const onAccent = onColor(accent);
  const focus = firstThatShows([accent, text], [background, card], UI_CONTRAST) ?? text;
  const accentInk = firstThatShows([accent, text], [background, card], BODY_TEXT_CONTRAST) ?? text;
  const hover = mixHex(accent, onAccent, 0.9);
  const button = design.shape.button;

  const vars: Record<string, string> = {
    '--color-surface-muted': background,
    '--color-surface': card,
    '--color-white': card,
    '--color-ink': text,
    '--color-ink-muted': muted,
    '--color-line': line,
    '--color-tile': mixHex(text, background, 0.14),
    ...Object.fromEntries(Object.entries(SLATE_STEPS).map(([step, weight]) => [`--color-slate-${step}`, mixHex(text, card, weight)])),
    '--brand-bg': background,
    '--brand-text': text,
    '--brand-primary': accent,
    '--brand-primary-foreground': onAccent,
    '--brand-secondary': branding.secondaryColor,
    '--brand-secondary-foreground': readableColor(branding.secondaryColor),
    '--action': accent,
    '--action-foreground': onAccent,
    '--action-hover': contrast(onAccent, hover) >= BODY_TEXT_CONTRAST ? hover : accent,
    '--portal-focus': focus,
    '--radius-chip': 'max(0px, calc(var(--radius-control) - 2px))',
  };
  if (button === 'pill') vars['--radius-button'] = '999px';
  if (button === 'block') vars['--radius-button'] = '0px';
  if (button === 'outline') {
    vars['--action'] = 'transparent';
    vars['--action-foreground'] = accentInk;
    vars['--action-hover'] = mixHex(accent, card, 0.18);
    vars['--action-edge'] = accent;
  }

  return { tone: dark ? 'dark' : 'light', background, card, text, muted, accent, onAccent, focus, vars };
}

function rule(selector: string, declarations: (string | false | null)[]): string {
  const body = declarations.filter((declaration): declaration is string => typeof declaration === 'string');
  return body.length > 0 ? `${selector} { ${body.join('; ')}; }` : '';
}

const UI_PARTS = "button, [role='tab'], [role='menuitem'], summary, nav a, label:not(:has(input, textarea, select))";
const UI_ELEMENTS = `:is(${UI_PARTS})`;
const UI_SIZED = `:is(${UI_PARTS}, [data-badge])`;

/**
 * What the design adds beyond colours: its faces and corners (the same sheet the member area has always
 * worn), the heading face with the design's case, weight and tracking, the UI face on buttons, tabs, menu
 * items and labels at the design's UI text size (badges take the size too), the site's scrollbars and form
 * fields, and a focus ring that shows on any colour. `:root.portal-look body` carries the body face to the
 * dialogs and menus that draw outside the page. A preview inside another page passes `page: false`, so the
 * page's own scrollbar stays that page's.
 */
export function portalLookCss(look: PortalLookDto, { page = true }: { page?: boolean } = {}): string {
  const type = look.design.typography;
  const theme = portalLookTheme(look);
  const body = fontStack(type.body);
  const heading = fontStack(type.heading);
  const ui = fontStack(type.ui);
  return [
    memberDesignCss(look.design, look.fonts),
    body && rule(':root.portal-look body', [`font-family: ${body}`]),
    rule('.portal-look :is(h1, h2, h3, h4)', [
      heading && `font-family: ${heading}`,
      type.headingCase && `text-transform: ${type.headingCase}`,
      type.headingWeight !== null && `font-weight: ${type.headingWeight}`,
      type.headingTracking !== null && `letter-spacing: ${type.headingTracking}em`,
    ]),
    rule(`.portal-look ${UI_ELEMENTS}`, [ui && `font-family: ${ui}`, type.uiCase && `text-transform: ${type.uiCase}`, type.uiTracking !== null && `letter-spacing: ${type.uiTracking}em`]),
    type.uiScale !== 1 ? rule('.portal-look', [`--site-ui-scale: ${type.uiScale}`]) : '',
    uiScaleCss(`.portal-look ${UI_SIZED}`, type.uiScale),
    uiBoldCss(`.portal-look ${UI_ELEMENTS}`, type),
    siteControlsCss(look.design, { scope: '.portal-look', host: 'portal', page, palette: { background: theme.background, text: theme.text, accent: theme.accent } }),
    rule('.portal-look :focus-visible', ['outline: 2px solid var(--portal-focus)', 'outline-offset: 2px']),
  ]
    .filter(Boolean)
    .join('\n');
}
